import { useEffect, useState, useRef, useCallback } from 'react';

import {
  Button,
  Table,
  TableBody,
  TableHead,
  TextField,
  TablePagination,
} from '@mui/material';

import {
  intVal,
  Nullable,
} from '@jamesgmarks/utilities';
import { getMany } from "../../../redux/features/dynamic/actions";

import { ISubscriptions } from '@llws/lift-entity-interfaces';

import { loadChargesLookup } from '../../../redux/features/lookups/actions';
import { StyledTableCell, StyledTableRow } from '../../parts/mui/StyledTables';
import { useAppSelector } from '../../../redux/hooks';
import { INestedWhereCommand } from "@llws/dynamic-router-utils";
import { LoadingSpinner } from 'src/components/parts/LoadingSpinner';
interface CreditModalProps {
  searchClientId: number;
  setSelectedSubscription: (subscription: Nullable<ISubscriptions>) => void;
  selectedSubscriptionIds?: number[];
}

const CreditSubscriptionSelection = ({
  searchClientId,
  setSelectedSubscription,
  selectedSubscriptionIds = [],
}: CreditModalProps) => {

  const clientSubscriptions = useAppSelector((state) => state.dynamic.data.subscriptions?.list);
  const totalRecords = useAppSelector((state) => state.dynamic.metaData?.totalRecords ?? 0);

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>(searchQuery);
  const debounceTimeoutRef = useRef<Nullable<NodeJS.Timeout>  >(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [sortColumn, setSortColumn] = useState<string>("id");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleSortSubscriptions = (column: string) => {
    const isAsc = sortColumn === column && sortDirection === "asc";
    const newDirection = isAsc ? "desc" : "asc";
    setSortDirection(newDirection);
    setSortColumn(column);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = intVal(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const fetchSubscriptions = useCallback(
    async (
      clientId: number,
      searchQuery: string,
      sortColumn: string,
      sortDirection: "asc" | "desc",
      page: number = 0,
      rowsPerPage: number = 10,
    ) => {
      setIsLoading(true);
      try {
        const whereCommands: INestedWhereCommand[] = [
          {
            condition: "and",
            whereCommands: [
              { field: "s.clientId", value: `${clientId}` },
            ],
          },
          {
            condition: "and",
            whereCommands: [
              { field: "s.invoiceDescription", value: `${searchQuery}`, operator: "like", condition: "or" },
              { field: "s.startDate", value: `${searchQuery}`, operator: "like", condition: "or" },
              { field: "s.expiryDate", value: `${searchQuery}`, operator: "like", condition: "or" },
              { field: "s.id", value: `${searchQuery}`, operator: "like", condition: "or" },
            ],
          },
        ];

        const data = await getMany("subscriptions", {
          alias: "s",
          relations: [{ path: "s.baseSubscription", alias: "bs" }],
          orderBy: [{ field: sortColumn, direction: sortDirection }],
          limit: rowsPerPage,
          page: page + 1,
          where: whereCommands,
        });
        return data;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page when searching
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(event.target.value);
    }, 300);
  };

  useEffect(() => {
    if (searchClientId) {
      fetchSubscriptions(searchClientId, debouncedSearchQuery, sortColumn, sortDirection, page, rowsPerPage);
    }
  }, [debouncedSearchQuery, fetchSubscriptions, searchClientId, sortColumn, sortDirection, page, rowsPerPage]);

  return (<>
    <TextField
      fullWidth
      label="Search subscriptions..."
      variant="outlined"
      onChange={handleSearchChange}
      sx={{ mb: 2 }}
      placeholder="Search by ID, description, start date, or expiry date"
    />
    {isLoading && (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <LoadingSpinner loadedState="loading" />
      </div>
    )}
    {!isLoading && <>
      <TablePagination
        component="div"
        count={totalRecords}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
      <div style={{ height: '400px', overflow: 'auto' }}>
        <Table style={{ width: '100%' }} stickyHeader>

          <TableHead>
            <StyledTableRow>
              <StyledTableCell onClick={() => handleSortSubscriptions("id")}>
                        ID
              </StyledTableCell>
              <StyledTableCell onClick={() => handleSortSubscriptions("invoiceDescription")}>
                        Description
              </StyledTableCell>
              <StyledTableCell onClick={() => handleSortSubscriptions("startDate")}>
                        Start
              </StyledTableCell>
              <StyledTableCell onClick={() => handleSortSubscriptions("expiryDate")}>
                        Expiry
              </StyledTableCell>
              <StyledTableCell>&nbsp;</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {
              (clientSubscriptions ?? []).map((sub, i) => {
                const hasSelectedCharges = sub.id != null && selectedSubscriptionIds.includes(sub.id);
                return (
                  <StyledTableRow
                    hover={true}
                    key={i}
                    customSx={hasSelectedCharges ? {
                      backgroundColor: '#edf7ed',
                      '&:hover': {
                        backgroundColor: '#dff0df',
                      },
                    } : undefined}
                  >
                    <StyledTableCell>{sub.id}</StyledTableCell>
                    <StyledTableCell>{sub.invoiceDescription}</StyledTableCell>
                    <StyledTableCell>{sub.startDate}</StyledTableCell>
                    <StyledTableCell>{sub.expiryDate}</StyledTableCell>
                    <StyledTableCell style={{ textAlign: 'right' }}>
                      <Button
                        size='small'
                        variant='contained'
                        color={hasSelectedCharges ? 'success' : 'primary'}
                        onClick={(e) => {
                          setSelectedSubscription(sub);
                          loadChargesLookup({ subscriptionId: sub.id ?? 0 });
                        }}
                      >
                        {hasSelectedCharges ? 'Selected' : 'Select'}
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })
            }
          </TableBody>
        </Table>
      </div>
    </>
    }
  </>
  );
};

export default CreditSubscriptionSelection;
